import { useRef, useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  Camera, UploadSimple, FilePdf, Images, X, Plus, Check, WarningCircle, CircleNotch,
  ArrowCounterClockwise, PencilSimple, Trash, CalendarBlank, ArrowRight, CaretDown, EnvelopeSimple, Copy, Airplane,
} from "@phosphor-icons/react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { savePendingScan, loadPendingScan, clearPendingScan, hasPendingScan } from "@/lib/pendingScan";
import { money, longDate } from "@/lib/format";
import { celebrateUnlocks } from "@/lib/celebrate";
import { CurrencyCombobox } from "@/components/CurrencyCombobox";
import { MerchantLogo } from "@/components/riq";
import { useAuth } from "@/context/AuthContext";
import { pushRecentCurrency } from "@/lib/recentCurrencies";
import { trackConversionOnce } from "@/lib/adsConversions";
import { Coins } from "@phosphor-icons/react";

const STEPS = [
  "Image preprocessing", "Running OCR", "Detecting merchant", "Extracting date",
  "Extracting items", "Reading quantities", "Reading prices", "Finding discounts",
  "Extracting VAT", "Extracting total", "Classifying categories", "Normalising products",
];

const INBOUND_ADDRESS = "scan@sliply.co.za";

function buildPayload(data) {
  return {
    merchant_name: data.merchant_name || "Unknown merchant",
    merchant_category: data.merchant_category || "Other",
    branch: data.branch, location: data.location,
    date: data.date || new Date().toISOString().slice(0, 10),
    time: data.time, currency: data.currency || "ZAR",
    subtotal: data.subtotal != null ? Number(data.subtotal) : null,
    discount: Number(data.discount) || 0,
    tax: data.tax != null ? Number(data.tax) : null,
    tip: data.tip != null ? Number(data.tip) : null,
    total: Number(data.total) || 0,
    payment_method: data.payment_method, reference_number: data.reference_number,
    storage_paths: data.storage_paths || [], ocr_text: data.ocr_text || "",
    ocr_engine: data.ocr_engine || null,
    confidence: data.confidence || {},
    source: data.source || null,
    items: (data.items || []).map((it) => ({
      description: it.description || it.canonical_name,
      canonical_name: it.canonical_name || it.description || "Item",
      brand: it.brand, size: it.size, unit: it.unit,
      category: it.category || data.merchant_category || "Other",
      quantity: Number(it.quantity) || 1,
      unit_price: it.unit_price != null ? Number(it.unit_price) : null,
      total_price: it.total_price != null ? Number(it.total_price) : null,
      confidence: it.confidence != null ? Number(it.confidence) : null,
    })),
  };
}

export default function AddReceiptModal({ open, onClose, onSaved }) {
  const { refresh, user } = useAuth();
  const homeCurrency = (user?.currency || "ZAR").toUpperCase();
  const [step, setStep] = useState("select"); // select | multi | processing | confirm | saving | saved
  const [activeStep, setActiveStep] = useState(0);
  const [extracted, setExtracted] = useState(null);
  const [draft, setDraft] = useState(null); // { merchant_name, date, total, currency }
  const [saved, setSaved] = useState(null); // { receipt, extracted }
  const [shots, setShots] = useState([]); // File[] for multi
  const [copied, setCopied] = useState(false);
  const inputRef = useRef();
  const pendingMode = useRef(null);
  const pendingSource = useRef("upload");
  const replaceIdRef = useRef(null);
  const resumedRef = useRef(false);

  useEffect(() => {
    if (!open) {
      setStep("select"); setSaved(null); setActiveStep(0); setShots([]);
      setExtracted(null); setDraft(null); replaceIdRef.current = null;
      resumedRef.current = false;
    }
  }, [open]);

  // Resume an interrupted scan (app reloaded after the camera backgrounded it).
  useEffect(() => {
    if (!open || resumedRef.current || !hasPendingScan()) return;
    resumedRef.current = true;
    (async () => {
      const files = await loadPendingScan();
      if (files && files.length) { toast.message("Resuming your scan…"); scanReceipt(files); }
      else clearPendingScan();
    })();
    // eslint-disable-next-line
  }, [open]);

  useEffect(() => {
    if (step !== "processing") return;
    const t = setInterval(() => setActiveStep((s) => Math.min(s + 1, STEPS.length - 1)), 500);
    return () => clearInterval(t);
  }, [step]);

  const trigger = (opts) => {
    pendingMode.current = opts.mode;
    pendingSource.current = opts.source || "upload";
    const el = inputRef.current;
    el.value = "";
    el.accept = opts.accept;
    el.multiple = !!opts.multiple;
    if (opts.capture) el.setAttribute("capture", "environment");
    else el.removeAttribute("capture");
    el.click();
  };

  const scanReceipt = async (files) => {
    setStep("processing"); setActiveStep(0);
    savePendingScan(files);
    const form = new FormData();
    files.forEach((f) => form.append("files", f));
    try {
      const { data } = await api.post("/receipts/process", form, { headers: { "Content-Type": "multipart/form-data" } });
      setExtracted(data);
      setDraft({
        merchant_name: data.merchant_name || "",
        date: data.date || new Date().toISOString().slice(0, 10),
        total: data.total != null ? String(data.total) : "",
        currency: data.currency || homeCurrency,
        items: (data.items || []).map((it) => ({
          description: it.description,
          canonical_name: it.canonical_name || it.description || "",
          brand: it.brand, size: it.size, unit: it.unit, category: it.category,
          quantity: it.quantity != null ? String(it.quantity) : "1",
          unit_price: it.unit_price != null ? String(it.unit_price) : "",
          total_price: it.total_price != null ? String(it.total_price) : "",
          confidence: it.confidence != null ? Number(it.confidence) : null,
        })),
      });
      setStep("confirm");
    } catch (err) {
      clearPendingScan();
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not read receipt");
      setStep(files.length > 1 ? "multi" : "select");
    }
  };

  const confirmSave = async () => {
    if (!draft.merchant_name.trim()) { toast.error("Please enter a vendor name"); return; }
    const amount = Number(draft.total);
    if (!amount || amount <= 0) { toast.error("Please enter a valid amount"); return; }
    setStep("saving");
    const payload = buildPayload({
      ...extracted,
      merchant_name: draft.merchant_name.trim(),
      date: draft.date,
      total: amount,
      currency: draft.currency,
      items: draft.items,
      source: pendingSource.current,
    });
    try {
      const { data: rec } = await api.post("/receipts", payload);
      pushRecentCurrency(draft.currency);
      const replaceId = replaceIdRef.current;
      replaceIdRef.current = null;
      if (replaceId) {
        try { await api.delete(`/receipts/${replaceId}`); }
        catch { toast.warning("Saved, but couldn't remove the previous copy — check your Receipts."); }
      }
      setSaved({ receipt: rec, extracted });
      setStep("saved");
      clearPendingScan();
      if (rec.review_status !== "rejected") trackConversionOnce("first_scan", "first_scan");
      if (rec.review_status === "rejected") toast.warning(rec.review_code === "duplicate" ? "Saved as a duplicate — it won't count and earns no Moolas." : "Receipt rejected.");
      else if (rec.review_status === "needs_date_fix") toast.warning("Saved — please confirm the purchase date (more than 2 months old).");
      else if (rec.moolas_earned > 0) toast.success(`Receipt saved · +${rec.moolas_earned} Moolas earned!`);
      else toast.success("Receipt saved");
      refresh();
      suggestTripForReceipt(rec);
      if (rec.unlocked?.length) celebrateUnlocks(rec.unlocked);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save receipt");
      setStep("confirm");
    }
  };

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (pendingMode.current === "multi-add") {
      setShots((s) => [...s, ...files]);
      return;
    }
    await scanReceipt(files);
  };

  const rescan = () => {
    setExtracted(null); setDraft(null);
    trigger({ accept: "image/*", capture: true, mode: "single", source: "scan" });
  };

  const retake = () => {
    replaceIdRef.current = saved?.receipt?.id || null;
    setSaved(null); setShots([]); setExtracted(null); setDraft(null);
    trigger({ accept: "image/*", capture: true, mode: "single", source: "scan" });
  };

  const discardSaved = async () => {
    if (saved?.receipt?.id) {
      try { await api.delete(`/receipts/${saved.receipt.id}`); } catch {}
      toast.success("Receipt removed");
    }
    handleClose();
  };

  const handleClose = () => { clearPendingScan(); resumedRef.current = false; onClose(); };

  // After a scan saves, if its date falls inside a trip's range, offer to assign it.
  const suggestTripForReceipt = async (rec) => {
    if (!rec?.date || rec.trip_id || rec.review_status === "rejected") return;
    try {
      const { data } = await api.get("/trips");
      const t = (data.trips || []).find(
        (tr) => tr.start_date && tr.end_date && rec.date >= tr.start_date && rec.date <= tr.end_date
      );
      if (!t) return;
      toast(`This looks like it's from "${t.name}"`, {
        description: "Assign this receipt to that trip?",
        icon: <Airplane size={18} weight="fill" className="text-riq-primary" />,
        duration: 9000,
        action: {
          label: "Assign",
          onClick: async () => {
            try {
              await api.patch(`/receipts/${rec.id}/meta`, { trip_id: t.id });
              toast.success(`Assigned to "${t.name}"`);
              refresh();
            } catch { toast.error("Could not assign to trip"); }
          },
        },
      });
    } catch { /* trips optional — ignore */ }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={handleClose} />
      <input ref={inputRef} type="file" hidden onChange={onFiles} data-testid="receipt-file-input" />
      <motion.div
        initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
        className="relative w-full sm:max-w-lg bg-white rounded-t-3xl sm:rounded-3xl border border-riq-border shadow-xl max-h-[92vh] overflow-y-auto"
      >
        <div className="sticky top-0 bg-white/95 backdrop-blur-xl flex items-center justify-between px-6 py-4 border-b border-riq-border z-10">
          <h3 className="font-head text-lg font-semibold text-riq-primary">
            {step === "select" && "Add Receipt"}
            {step === "multi" && "Scan a long receipt"}
            {step === "processing" && "Reading your receipt…"}
            {step === "confirm" && "Confirm details"}
            {step === "saving" && "Saving…"}
            {step === "saved" && "Receipt saved"}
          </h3>
          <button onClick={handleClose} data-testid="close-add-receipt" className="text-neutral-400 hover:text-neutral-700"><X size={22} /></button>
        </div>

        <div className="p-6 safe-pb">
          <AnimatePresence mode="wait">
            {step === "select" && (
              <motion.div key="select" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <div className="grid grid-cols-2 gap-3">
                  <SelectTile testId="capture-camera" icon={Camera} label="Camera" hint="Scan a receipt" onClick={() => trigger({ accept: "image/*", capture: true, mode: "single", source: "scan" })} />
                  <SelectTile testId="capture-upload" icon={UploadSimple} label="Upload Image" hint="JPG / PNG" onClick={() => trigger({ accept: "image/*", mode: "single", source: "upload" })} />
                  <SelectTile testId="capture-pdf" icon={FilePdf} label="Upload PDF" hint="Digital receipt" onClick={() => trigger({ accept: "application/pdf,image/*", mode: "single", source: "pdf" })} />
                  <SelectTile testId="capture-multi" icon={Images} label="Multiple Images" hint="Long receipt" onClick={() => { setShots([]); setStep("multi"); }} />
                </div>
                <div data-testid="email-in-banner" className="mt-3 rounded-2xl border border-riq-border bg-riq-sage/40 p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center shrink-0"><EnvelopeSimple size={20} weight="duotone" className="text-riq-primary" /></div>
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-sm text-riq-primary">Email it in</div>
                      <p className="text-xs text-muted-foreground leading-snug mt-0.5">Forward any receipt email (PDF or in the body) from your Sliply address and we'll scan it automatically.</p>
                      <div className="mt-2 flex items-center gap-2">
                        <code data-testid="email-in-address" className="flex-1 truncate rounded-lg bg-white border border-riq-border px-2.5 py-1.5 text-xs font-mono text-riq-primary">{INBOUND_ADDRESS}</code>
                        <button
                          data-testid="email-in-copy"
                          onClick={() => { navigator.clipboard?.writeText(INBOUND_ADDRESS); setCopied(true); setTimeout(() => setCopied(false), 1500); toast.success("Address copied"); }}
                          className="shrink-0 rounded-lg bg-riq-primary text-white px-3 py-1.5 text-xs font-semibold flex items-center gap-1"
                        >
                          {copied ? <Check size={13} weight="bold" /> : <Copy size={13} weight="bold" />} {copied ? "Copied" : "Copy"}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {step === "multi" && (
              <motion.div key="multi" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                  Snap each part of a long receipt from top to bottom. Add another section for every part, then process — our AI stitches them into one receipt, reading from the first item down to the final total.
                </p>
                <div className="grid grid-cols-3 gap-2 mb-4">
                  {shots.map((f, i) => (
                    <div key={i} data-testid={`multi-shot-${i}`} className="relative aspect-[3/4] rounded-xl overflow-hidden border border-riq-border bg-secondary">
                      <img src={URL.createObjectURL(f)} alt={`Section ${i + 1}`} className="w-full h-full object-cover" />
                      <span className="absolute top-1 left-1 bg-black/60 text-white text-[10px] rounded-full px-1.5 py-0.5">{i + 1}</span>
                      <button onClick={() => setShots((s) => s.filter((_, j) => j !== i))} className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-0.5"><X size={12} /></button>
                    </div>
                  ))}
                  <button data-testid="multi-add-tile" onClick={() => trigger({ accept: "image/*", capture: true, mode: "multi-add", source: "multi" })} className="aspect-[3/4] rounded-xl border-2 border-dashed border-riq-border flex flex-col items-center justify-center gap-1 text-riq-primary hover:border-riq-primary transition-colors">
                    <Plus size={22} weight="bold" /><span className="text-[11px] font-medium">Add section</span>
                  </button>
                </div>
                <div className="flex gap-2">
                  <button data-testid="multi-add-upload" onClick={() => trigger({ accept: "image/*", multiple: true, mode: "multi-add", source: "multi" })} className="flex-1 rounded-full border border-riq-border py-3 text-sm font-medium hover:border-riq-primary flex items-center justify-center gap-2"><UploadSimple size={16} /> Upload images</button>
                  <button data-testid="multi-process-btn" disabled={shots.length === 0} onClick={() => scanReceipt(shots)} className="flex-[1.5] rounded-full bg-riq-primary text-white py-3 text-sm font-medium disabled:opacity-50 flex items-center justify-center gap-2">Process {shots.length || ""} section{shots.length === 1 ? "" : "s"} <ArrowRight size={16} weight="bold" /></button>
                </div>
              </motion.div>
            )}

            {step === "processing" && (
              <motion.div key="processing" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <div className="relative h-40 rounded-2xl bg-riq-primary/5 border border-riq-border overflow-hidden mb-6 flex items-center justify-center">
                  <div className="scan-beam" />
                  <Receiptish />
                </div>
                <div className="space-y-2">
                  {STEPS.map((s, i) => (
                    <div key={s} className="flex items-center gap-3 text-sm">
                      <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] ${i < activeStep ? "bg-riq-primary2 text-white" : i === activeStep ? "bg-riq-gold text-riq-primary" : "bg-secondary text-neutral-400"}`}>
                        {i < activeStep ? <Check size={12} weight="bold" /> : i === activeStep ? <CircleNotch size={12} className="animate-spin" weight="bold" /> : i + 1}
                      </span>
                      <span className={i <= activeStep ? "text-neutral-800" : "text-neutral-400"}>{s}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}

            {step === "confirm" && draft && (
              <motion.div key="confirm" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                <div className="flex items-center gap-3 mb-5">
                  <MerchantLogo name={draft.merchant_name || "Receipt"} size={48} />
                  <p className="text-sm text-muted-foreground leading-snug">We read your receipt — please confirm the details below before saving.</p>
                </div>

                <div className="space-y-4">
                  <Field label={<>Vendor <ConfPct pct={extracted?.merchant_name?.trim() ? extracted?.confidence?.merchant : 0} /></>}>
                    <input
                      data-testid="confirm-vendor-input"
                      value={draft.merchant_name}
                      onChange={(e) => setDraft({ ...draft, merchant_name: e.target.value })}
                      className="riq-input"
                      style={confStyle(extracted?.merchant_name?.trim() ? extracted?.confidence?.merchant : 0)}
                      placeholder="Store / vendor name"
                    />
                    {!extracted?.merchant_name?.trim() && (
                      <p data-testid="vendor-fallback-hint" className="mt-1.5 text-[11px] font-medium text-[#B3261E] flex items-center gap-1">
                        <WarningCircle size={13} weight="fill" /> Couldn't read the vendor — please enter it.
                      </p>
                    )}
                  </Field>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label={<>Date <ConfPct pct={extracted?.date ? extracted?.confidence?.date : 0} /></>}>
                      <input
                        data-testid="confirm-date-input" type="date"
                        value={draft.date}
                        onChange={(e) => setDraft({ ...draft, date: e.target.value })}
                        className="riq-input font-semibold"
                        style={confStyle(extracted?.date ? extracted?.confidence?.date : 0)}
                      />
                      {!extracted?.date && (
                        <p data-testid="date-fallback-hint" className="mt-1.5 text-[11px] font-medium text-[#B3261E] flex items-center gap-1">
                          <WarningCircle size={13} weight="fill" /> Couldn't read the date — set to today. Please check.
                        </p>
                      )}
                    </Field>
                    <Field label="Currency">
                      <CurrencyCombobox
                        testId="confirm-currency"
                        value={draft.currency}
                        onChange={(c) => setDraft({ ...draft, currency: c })}
                      />
                    </Field>
                  </div>
                  <Field label={<>Total amount <ConfPct pct={extracted?.total != null ? extracted?.confidence?.total : 0} /></>}>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-base font-head font-bold text-neutral-500">{draft.currency}</span>
                      <input
                        data-testid="confirm-amount-input" type="number" step="0.01" inputMode="decimal"
                        value={draft.total}
                        onChange={(e) => setDraft({ ...draft, total: e.target.value })}
                        className="riq-input !pl-16 !py-4 !text-3xl font-head font-extrabold text-riq-primary tracking-tight"
                        style={confStyle(extracted?.total != null ? extracted?.confidence?.total : 0)}
                        placeholder="0.00"
                      />
                    </div>
                    {extracted?.total == null && (
                      <p data-testid="amount-fallback-hint" className="mt-1.5 text-[11px] font-medium text-[#B3261E] flex items-center gap-1">
                        <WarningCircle size={13} weight="fill" /> Couldn't read the amount — please enter the total.
                      </p>
                    )}
                  </Field>
                </div>

                <div className="mt-5">
                  <LineItemsEditor
                    items={draft.items}
                    currency={draft.currency}
                    onChange={(items) => setDraft({ ...draft, items })}
                    category={extracted?.merchant_category || "Other"}
                  />
                </div>

                {extracted?.possible_duplicate && (
                  <div data-testid="confirm-duplicate-warning" className="mt-3 flex gap-3 items-start rounded-xl bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800">
                    <WarningCircle size={20} weight="fill" className="shrink-0 mt-0.5" />
                    <div>This looks like a possible duplicate of an earlier receipt. Save only if it's a new purchase.</div>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3 mt-6">
                  <button data-testid="confirm-retake-btn" onClick={rescan} className="rounded-full border border-riq-border py-3 text-sm font-medium hover:border-riq-primary flex items-center justify-center gap-2"><ArrowCounterClockwise size={16} /> Retake</button>
                  <button data-testid="confirm-save-btn" onClick={confirmSave} className="rounded-full bg-riq-primary text-white py-3 text-sm font-medium flex items-center justify-center gap-2"><Check size={16} weight="bold" /> Confirm &amp; Save</button>
                </div>
              </motion.div>
            )}

            {step === "saving" && (
              <motion.div key="saving" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="py-14 flex flex-col items-center justify-center gap-3">
                <CircleNotch size={36} className="animate-spin text-riq-primary" weight="bold" />
                <p className="text-sm text-muted-foreground">Saving your receipt…</p>
              </motion.div>
            )}

            {step === "saved" && saved && (
              <motion.div key="saved" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                <div data-testid="saved-summary" className="rounded-2xl border border-riq-border p-5 flex items-center gap-4">
                  <MerchantLogo name={saved.receipt.merchant_name} size={56} />
                  <div className="flex-1 min-w-0">
                    <div data-testid="saved-merchant" className="font-head text-lg font-bold text-riq-primary truncate">{saved.receipt.merchant_name}</div>
                    <div className="text-xs text-muted-foreground flex items-center gap-1.5 mt-0.5"><CalendarBlank size={13} /> {longDate(saved.receipt.date)}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{saved.receipt.item_count} item{saved.receipt.item_count === 1 ? "" : "s"} · {saved.receipt.merchant_category}</div>
                  </div>
                  <div data-testid="saved-total" className="font-head text-2xl font-bold text-riq-primary text-right">
                    {money(saved.receipt.total, saved.receipt.currency)}
                    {saved.receipt.is_foreign && (
                      <div className="text-[11px] font-normal text-muted-foreground mt-0.5">{money(saved.receipt.original_total, saved.receipt.original_currency)} {saved.receipt.original_currency}</div>
                    )}
                  </div>
                </div>

                {saved.receipt.moolas_earned > 0 && (
                  <div data-testid="moolas-earned" className="mt-3 flex items-center justify-center gap-2 rounded-xl bg-riq-gold/15 border border-riq-gold/40 py-2.5 text-sm font-semibold text-riq-primary">
                    <Coins size={18} weight="fill" className="text-riq-gold" /> +{saved.receipt.moolas_earned} Moolas earned!
                  </div>
                )}

                {saved.receipt.review_status && saved.receipt.review_status !== "approved" && (
                  <div data-testid="saved-review-banner" className={`mt-3 flex gap-3 items-start rounded-xl p-3 text-sm ${saved.receipt.review_status === "rejected" ? "bg-[#FFE3E1] border border-[#f3b1ab] text-[#b3261e]" : "bg-riq-gold/15 border border-riq-gold/40 text-[#8a5a00]"}`}>
                    <WarningCircle size={20} weight="fill" className="shrink-0 mt-0.5" />
                    <div>
                      {saved.receipt.review_reason}
                      {saved.receipt.review_code !== "duplicate" && <> <button onClick={() => onSaved(saved.receipt)} className="underline font-medium">Correct the date</button></>}
                      {saved.receipt.review_code === "duplicate" && <> <button onClick={discardSaved} className="underline font-medium">Remove it</button></>}
                    </div>
                  </div>
                )}

                {!saved.receipt.review_status && saved.extracted?.possible_duplicate && (
                  <div data-testid="duplicate-warning" className="mt-3 flex gap-3 items-start rounded-xl bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800">
                    <WarningCircle size={20} weight="fill" className="shrink-0 mt-0.5" />
                    <div>This looks like a possible duplicate of an earlier receipt. <button onClick={discardSaved} className="underline font-medium">Remove it</button></div>
                  </div>
                )}

                <p className="text-sm text-muted-foreground text-center mt-5 mb-4">Not the right shot? Retake to replace it.</p>
                <div className="grid grid-cols-2 gap-3">
                  <button data-testid="retake-btn" onClick={retake} className="rounded-full border border-riq-border py-3 text-sm font-medium hover:border-riq-primary flex items-center justify-center gap-2"><ArrowCounterClockwise size={16} /> Retake</button>
                  <button data-testid="saved-edit-btn" onClick={() => onSaved(saved.receipt)} className="rounded-full bg-riq-primary text-white py-3 text-sm font-medium flex items-center justify-center gap-2"><PencilSimple size={16} /> Review & edit</button>
                </div>
                <button data-testid="saved-done-btn" onClick={handleClose} className="w-full mt-3 text-sm text-muted-foreground font-medium py-2">Done</button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
}

function Receiptish() {
  return (
    <div className="w-24 h-32 bg-white rounded-md shadow-inner border border-riq-border p-2 space-y-1.5">
      {[90, 70, 80, 55, 75, 40].map((w, i) => (
        <div key={i} className="h-1.5 rounded bg-neutral-200" style={{ width: `${w}%` }} />
      ))}
    </div>
  );
}

function SelectTile({ icon: Icon, label, hint, onClick, testId }) {
  return (
    <button data-testid={testId} onClick={onClick} className="flex flex-col items-start gap-2 rounded-2xl border border-riq-border p-5 text-left hover:border-riq-primary hover:-translate-y-0.5 transition-all duration-200">
      <div className="w-11 h-11 rounded-xl bg-riq-sage/60 flex items-center justify-center"><Icon size={24} weight="duotone" className="text-riq-primary" /></div>
      <div className="font-medium text-sm">{label}</div>
      <div className="text-xs text-muted-foreground">{hint}</div>
    </button>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">{label}</div>
      {children}
    </div>
  );
}

function confTier(pct) {
  if (pct >= 95) return { color: "#1f7a48", bg: "#E4F7ED", border: "#42C97C", ring: "rgba(66,201,124,.20)" };
  if (pct >= 85) return { color: "#8a5a00", bg: "#FFF1D6", border: "#F5A623", ring: "rgba(245,166,35,.22)" };
  return { color: "#b3261e", bg: "#FFE3E1", border: "#FF6B6B", ring: "rgba(255,107,107,.22)" };
}

function confStyle(pct) {
  if (pct == null) return undefined;
  const t = confTier(pct);
  return { borderColor: t.border, boxShadow: `0 0 0 3px ${t.ring}` };
}

function ConfPct({ pct }) {
  if (pct == null) return null;
  const t = confTier(pct);
  return (
    <span data-testid="conf-badge" style={{ color: t.color, background: t.bg }} className="ml-2 rounded-full px-2 py-0.5 text-[10px] font-bold align-middle">
      {Math.round(pct)}%
    </span>
  );
}

function LineItemsEditor({ items, currency, onChange, category }) {
  const lowCount = items.filter((it) => it.confidence != null && it.confidence < 85).length;
  const [open, setOpen] = useState(true);

  const update = (i, patch) => onChange(items.map((it, j) => (j === i ? { ...it, ...patch } : it)));
  const remove = (i) => onChange(items.filter((_, j) => j !== i));
  const add = () =>
    onChange([...items, { canonical_name: "", description: "", quantity: "1", unit_price: "", total_price: "", category, confidence: null }]);

  return (
    <div className="rounded-2xl border border-riq-border overflow-hidden">
      <button
        type="button"
        data-testid="line-items-toggle"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-3 bg-secondary/40"
      >
        <span className="text-sm font-semibold text-riq-primary">
          Line items <span className="text-muted-foreground font-normal">({items.length})</span>
          {lowCount > 0 && (
            <span data-testid="line-items-low-count" className="ml-2 rounded-full px-2 py-0.5 text-[10px] font-bold" style={{ color: "#b3261e", background: "#FFE3E1" }}>
              {lowCount} to check
            </span>
          )}
        </span>
        <CaretDown size={16} weight="bold" className={`text-neutral-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="p-3 space-y-2.5">
          {items.length === 0 && (
            <p className="text-xs text-muted-foreground text-center py-2">No line items were read.</p>
          )}
          {items.map((it, i) => {
            const pct = it.confidence;
            const low = pct != null && pct < 85;
            return (
              <div
                key={i}
                data-testid={`confirm-item-${i}`}
                className="rounded-xl border p-3 bg-white"
                style={low ? confStyle(pct) : { borderColor: "var(--riq-border, #e5e7eb)" }}
              >
                <div className="flex items-center gap-2 mb-2">
                  <input
                    data-testid={`confirm-item-name-${i}`}
                    value={it.canonical_name || ""}
                    onChange={(e) => update(i, { canonical_name: e.target.value })}
                    placeholder="Item name"
                    className="riq-input !py-2 !text-sm flex-1"
                  />
                  {pct != null && (() => { const t = confTier(pct); return (
                    <span data-testid={`confirm-item-conf-badge-${i}`} style={{ color: t.color, background: t.bg }} className="rounded-full px-2 py-0.5 text-[10px] font-bold shrink-0">{Math.round(pct)}%</span>
                  ); })()}
                  <button type="button" data-testid={`confirm-item-remove-${i}`} onClick={() => remove(i)} className="text-neutral-400 hover:text-[#b3261e] shrink-0">
                    <Trash size={16} />
                  </button>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <LabeledMini label="Qty">
                    <input data-testid={`confirm-item-qty-${i}`} type="number" step="0.01" inputMode="decimal" value={it.quantity ?? ""} onChange={(e) => update(i, { quantity: e.target.value })} className="riq-input !py-2 !text-sm" placeholder="1" />
                  </LabeledMini>
                  <LabeledMini label="Unit">
                    <input data-testid={`confirm-item-unit-${i}`} type="number" step="0.01" inputMode="decimal" value={it.unit_price ?? ""} onChange={(e) => update(i, { unit_price: e.target.value })} className="riq-input !py-2 !text-sm" placeholder="0.00" />
                  </LabeledMini>
                  <LabeledMini label={`Total (${currency})`}>
                    <input data-testid={`confirm-item-total-${i}`} type="number" step="0.01" inputMode="decimal" value={it.total_price ?? ""} onChange={(e) => update(i, { total_price: e.target.value })} className="riq-input !py-2 !text-sm" placeholder="0.00" />
                  </LabeledMini>
                </div>
                {low && (
                  <p className="mt-2 text-[11px] font-medium text-[#B3261E] flex items-center gap-1">
                    <WarningCircle size={13} weight="fill" /> Low confidence — please check this item.
                  </p>
                )}
              </div>
            );
          })}
          <button type="button" data-testid="confirm-item-add" onClick={add} className="w-full rounded-xl border border-dashed border-riq-border py-2.5 text-sm font-medium text-riq-primary hover:border-riq-primary flex items-center justify-center gap-1.5">
            <Plus size={15} weight="bold" /> Add item
          </button>
        </div>
      )}
    </div>
  );
}

function LabeledMini({ label, children }) {
  return (
    <div>
      <div className="text-[10px] font-medium text-muted-foreground mb-1">{label}</div>
      {children}
    </div>
  );
}